<template>
  <div
      class="base-data-table"
      :class="{
      'with-actions': hasActions,
      'with-additional': hasAdditionalContent,
      'table-invalid': hasError,
      inverted,
    }"
      :style="{
      '--table-grid-template-columns': gridTemplate,
      '--table-width': `${tableWidth}px`,
      '--table-additional-width': `${additionalWidth}px`,
      '--table-actions-width': `${tableActionsWidth}px`,
    }"
  >
    <BaseModal
        v-if="modal.active"
        :title="modal.title"
        min-height="none"
        @close="close"
    >
      <template #content>
        <component
            :is="modal.component"
            v-bind="modal.props"
            @close="close"
            @clearSelection="clearSelection"
        />
      </template>
    </BaseModal>
    <BaseFilter
        v-if="filterData.length && endpoint"
        v-model="internalFilterValue"
        :filters="filterData"
        v-bind="{
        cache,
        tableSlug,
      }"
        :disable-search="disableSearch"
        :base-table-mounted="isMounted"
        @interaction="filterInteraction = true"
    />
    <div
        ref="table"
        class="base-data-table__table"
        :aria-busy="isBusy"
    >
      <loading
          :active.sync="isBusy"
          loader="dots"
          blur="20px"
          :is-full-page="false"
      />

      <div
          v-if="isCheckable && !disableCheckAll"
          class="flex gap-4 mb-4"
      >
        <BaseButtonV2
            v-if="totalItems > selected.length"
            type="outlined"
            small
            aria-label="Alle auswählen"
            aria-description="Wählt alle Einträge auf der aktuellen Seite aus"
            @click="selectAll"
            @keydown.enter="selectAll"
            @keydown.space="selectAll"
        >
          {{ checkAllLabel }}
        </BaseButtonV2>
        <BaseButtonV2
            v-if="selected.length"
            type="outlined"
            small
            aria-label="Alle abwählen"
            aria-description="Entfernt alle ausgewählten Einträge"
            @click.prevent="clearSelection"
            @keydown.enter="clearSelection"
            @keydown.space="clearSelection"
        >
          Alle abwählen
        </BaseButtonV2>
      </div>
      <div
          v-if="show"
          class="base-data-table__content"
          :class="{ 'with-actions': hasActions && visibleFields.length }"
          aria-label="Datentabelle"
      >
        <template v-if="!hideHeader">
          <template v-for="(field, index) in visibleFields">
            <div
                v-if="!isActionKey(field.key) && hasItem(field)"
                :key="`${field.key}-label`"
                :data-field="field.key"
                class="table-item table-item__label"
                :class="{
                'first-field': index === 0,
                'last-field': isLastItem(index),
                [field.class]: field.class,
              }"
                :style="`grid-column:${index + 1}; grid-row:1`"
                role="columnheader"
                :aria-sort="getSorting(field) === 'asc' ? 'ascending' : (getSorting(field) === 'desc' ? 'descending' : 'none')"
            >
              <div
                  v-if="isCheckable && index === 0"
                  :key="field.key"
                  class="flex items-center"
              >
                <BaseCheckbox
                    name="all"
                    :value="selectedAllOnPage"
                    aria-label="Alle auswählen"
                    aria-description="Wählt alle Einträge auf der aktuellen Seite aus"
                    @update="changeSelected('all', currentItems)"
                    @keydown.enter="changeSelected('all', currentItems)"
                    @keydown.space="changeSelected('all', currentItems)"
                />
                <div
                    v-if="enableOrderBy && field.disableOrderBy !== true"
                    :key="`sort-icon-${field.key}-${getSorting(field)}`"
                    class="table-item__label__sort-icon"
                    :data-order="getSorting(field)"
                    role="button"
                    tabindex="0"
                    :aria-label="`Sortiere nach ${field.label || field.value}`"
                    aria-description="Sortiert die Spalte nach dem angegebenen Kriterium"
                    @click="sort(field)"
                    @keypress.enter.prevent="sort(field)"
                    @keydown.space.prevent="sort(field)"
                >
                  <span v-show="getSorting(field) !== 'none'">
                    <i class="fa-duotone fa-sort" />
                  </span>
                  <span v-show="getSorting(field) === 'none'">
                    <i class="fa-solid fa-sort" />
                  </span>
                </div>
                <span
                    @click="sort(field)"
                    @keypress.enter="sort(field)"
                    v-html="field.label || field.value"
                />
              </div>
              <div
                  v-else-if="`${field.description || ''}`.trim() !== '' && !checkable && field.key === 'id'"
                  :title="field.description"
                  v-html="`${field.label || field.value} <i class='fas fa-question-circle'></i>`"
              />
              <div
                  v-else
                  class="flex items-center"
              >
                <span
                    v-if="enableOrderBy && field.disableOrderBy !== true"
                    :key="`sort-icon-${field.key}-${getSorting(field)}`"
                    class="table-item__label__sort-icon"
                    :data-order="getSorting(field)"
                    role="button"
                    tabindex="0"
                    :aria-label="`Sortiere nach ${field.label || field.value}`"
                    @click="sort(field)"
                    @keypress.enter="sort(field)"
                >
                  <span v-show="getSorting(field) !== 'none'">
                    <i class="fa-duotone fa-sort" />
                  </span>
                  <span v-show="getSorting(field) === 'none'">
                    <i class="fa-solid fa-sort" />
                  </span>
                </span>
                <span
                    @click="sort(field)"
                    @keypress.enter="sort(field)"
                    v-html="field.label || field.value"
                />
              </div>
            </div>
          </template>
          <div
              v-if="allowColumnReorder"
              ref="tableAction"
              class="table-item table-item__action no-shadow gear column-reorder accessibility-outline outline-inset"
              :style="`grid-column:${visibleFields.length + 1};grid-row: 1`"
              role="button"
              tabindex="0"
              :aria-label="`Spaltenreihenfolge ändern`"
              aria-description="Öffnet das Dialogfenster zur Änderung der Spaltenreihenfolge"
              @click="columnOrder = true"
              @keydown.enter.prevent="columnOrder = true"
              @keydown.space.prevent="columnOrder = true"
          >
            <span><i class="fa-solid fa-gear" /></span>
          </div>
        </template>
        <template v-for="(item, index) in currentItems">
          <template v-for="(field, fieldIndex) in visibleFields">
            <div
                v-if="!isActionKey(field.key)"
                :key="`${field.key}-${index}`"
                class="table-item table-item__data"
                :class="{
                'first-field': index === 0,
                'last-field': isLastItem(index),
                'last-row': isLastRow(index),
                [field.class]: field.class,
                [getRowClass(item, index)]: getRowClass(item, index),
              }"
                :style="`grid-column:${fieldIndex + 1};grid-row:${getGridRow(index)}`"
                role="cell"
            >
              <slot
                  :name="field.key"
                  :items="currentItems"
                  :item="item"
                  :field="field"
              >
                <div
                    v-if="isCheckable && fieldIndex === 0"
                    class="flex items-center"
                >
                  <BaseCheckbox
                      :name="item.id"
                      :value="!!selected.find(selection => selection === item.id)"
                      @update="changeSelected(item.id, $event)"
                  />
                  <span
                      v-if="!field.hideLabel"
                      :title="item[field.key]"
                      v-html="item[field.key]"
                  />
                </div>
                <div
                    v-else-if="field.key === 'id'"
                    :title="item[field.key]"
                    v-html="item[field.key]"
                />
                <Status
                    v-else-if="field.key === 'status'"
                    :status-code="item.status"
                    :statuses="statuses"
                    :selector="!!(selectable && selectable.status)"
                    @select="changeStatus({ status: $event, route: selectable.status.route, id: item.id })"
                />
                <div
                    v-else
                    v-html="typeof field.formatter === 'function' ? field.formatter(item[field.key]) : item[field.key]"
                />
              </slot>
            </div>
          </template>
          <div
              v-if="allowColumnReorder && !hasActions"
              :key="`action-${currentPage}-${index}-${currentNumItems || 0}`"
              class="table-item__action"
              :class="{
              [getRowClass(item, index)]: getRowClass(item, index),
            }"
              :style="`grid-column:${visibleFields.length + 1};grid-row:${getGridRow(index)}/${getGridRow(index) + 2}`"
          />
          <div
              v-if="hasActions"
              :key="`action-${currentPage}-${index}-${currentNumItems || 0}`"
              class="table-item__action"
              :class="{
              [getRowClass(item, index)]: getRowClass(item, index),
            }"
              :style="`grid-column:${visibleFields.length + 1};grid-row:${getGridRow(index)}/${getGridRow(index) + 2}`"
          >
            <slot
                name="action"
                :items="currentItems"
                :item="item"
            >
              <template v-if="getItemActionKey(item) === 'actions'">
                <BaseContextButton
                    :id="`base-button-${IOH.randomId()}`"
                    :actions="item.actions || []"
                    :color="actionColor"
                    :disabled="!itemHasActions(item)"
                    @action="action($event, item)"
                    @clear="action('clear', $event)"
                />
              </template>
              <template v-if="getItemActionKey(item) === 'html_actions'">
                <BaseContextButton
                    :content="item.html_actions || ''"
                    :color="actionColor"
                    :disabled="!itemHasActions(item)"
                />
              </template>
              <template v-if="getItemActionKey(item) === 'html_extra'">
                <div
                    class="html-extra"
                    v-html="item.html_extra"
                />
              </template>
            </slot>
          </div>
          <div
              :key="`table-item-${currentPage}-${index}-${currentNumItems || 0}`"
              class="table-item table-item__data-additional"
              :style="`grid-row:${getGridRow(index) + 1}/${getGridRow(index) + 1}`"
              :class="{
              'last-row': isLastRow(index),
            }"
          >
            <div :key="`additional-content-${currentPage}-${index}-${currentNumItems || 0}-${JSON.stringify(Object.values(item))}`">
              <slot
                  name="additionalContent"
                  :items="currentItems"
                  :item="item"
              />
            </div>
          </div>
        </template>
        <div
            v-if="showEmptyMessage"
            class="table-item__empty"
        >
          {{ emptyMessage }}
        </div>
      </div>
      <div
          v-if="hasError"
          class="table-hint"
      >
        <div
            v-if="hasError"
            class="table-hint--error"
            v-html="errorMessage"
        />
      </div>
      <div
          v-if="(isAsync || pagination)"
          class="io-data-table__pagination"
      >
        <div>
          <select
              v-model="perPage"
              class="me-4 io-input-sm accessibility-outline"
              aria-label="Einträge pro Seite"
              aria-description="Wählt die Anzahl der Einträge pro Seite"
              @input="savePerPage"
          >
            <option value="10">
              10 Einträge anzeigen
            </option>
            <option value="25">
              25 Einträge anzeigen
            </option>
            <option value="50">
              50 Einträge anzeigen
            </option>
          </select>
          <span class="d-none d-lg-inline">
            {{ Math.max(0, currentNumItems - itemCountOffset) }} von {{ totalItems }} Einträgen
          </span>
        </div>
        <BasePagination
            :current-page="currentPage"
            :length="pageCount"
            @change="change($event)"
        />
      </div>
    </div>
    <ColumnOrder
        :show="columnOrder"
        :columns="existingColumns"
        @change="changeOrder"
        @close="columnOrder = false"
    />
    <RowOrder
        :show="rowOrder"
        :rows="rows"
        :columns="existingRows"
        @change="changeRowOrder"
        @close="closeRowOrderModal"
    />
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import { snakeCase } from 'lodash';

import Loading from 'vue-loading-overlay';
import ColumnOrder from './table/ColumnOrder.vue';
import RowOrder from './table/RowOrder.vue';
import Status from '../../order/table/Status.vue';
import IoConfirmation from '../../center/components/IoConfirmation.vue';
import slugify from '../../mixins/slugify';
import appIDB from '@/apps/order/classes/AppIDB';

const NOT_EDITABLE_ACTIONS = [
  'edit',
  'clear',
  'delete',
  'destroy',
  'clearAndSync',
  'destroyWithUsers',
];

const ACTION_KEYS = [
  'actions',
  'html_actions',
  'html_extra',
];

export default {
  name: 'BaseTable',

  components: {
    Loading,
    ColumnOrder,
    RowOrder,
    Status,
    IoConfirmation,
  },

  model: {
    prop: 'value',
    event: 'update',
  },

  props: {
    filter: {
      type: Array,
      default: () => ([]),
    },
    filterValue: {
      type: Object,
      default: () => ({}),
    },
    initialFilterInteraction: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Boolean,
      default: false,
    },
    checked: {
      type: Array,
      default: () => ([]),
    },
    value: {
      type: String,
      default: '',
    },
    checkable: {
      type: Boolean,
      default: false,
    },
    disableCheckAll: {
      type: Boolean,
      default: false,
    },
    checkAll: {
      type: String,
      default: null,
    },
    checkAllParams: {
      type: Object,
      default: () => ({}),
    },
    checkAllLabel: {
      type: String,
      default: 'Alle auswählen',
    },
    fields: {
      required: true,
      type: [Object, Array],
    },
    endpoint: {
      type: String,
      default: null,
    },
    endpointParams: {
      type: Object,
      default: () => ({}),
    },
    sid: {
      type: String,
      default: null,
    },
    search: {
      type: String,
      default: '',
    },
    disableSearch: {
      type: Boolean,
      default: false,
    },
    theme: {
      type: String,
      default: 'striped',
    },
    items: {
      type: [Array, Object],
      default: null,
    },
    error: {
      type: String,
      default: null,
    },
    sticky: {
      type: Boolean,
      default: true,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
      validator: (val) => [10, 25, 50].indexOf(val) !== -1,
    },
    actionColor: {
      type: String,
      default: 'primary',
    },
    disableActions: {
      type: Boolean,
      default: false,
    },
    enableOrderBy: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: 'Keine Einträge vorhanden.',
    },
    rowOrderPopup: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: String,
      default: null,
    },
    hiddenColumns: {
      type: Object,
      default: () => ({}),
    },
    additionalFixed: {
      type: Boolean,
      default: true,
    },
    allowColumnReorder: {
      type: Boolean,
      default: true,
    },
    inverted: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    rowClass: {
      type: [Function, String],
      default: null,
    },
    itemCountOffset: {
      type: Number,
      default: 0,
    },
  },

  data() {
    const defaultDeleteConfirmation = {
      component: IoConfirmation,
      title: 'Löschen bestätigen',
    };

    if (typeof window.baseTableNum === 'number') {
      window.baseTableNum += 1;
    } else {
      window.baseTableNum = 0;
    }

    return {
      showActions: false,
      filterInteraction: this.initialFilterInteraction,
      actionKeys: ACTION_KEYS,
      selected: [],
      errorMessage: null,
      pageSelection: [],
      currentPage: 1,
      perPage: 10,
      isBusy: false,
      fullPageLoader: false,
      currentNumItems: 0,
      currentItems: [],
      totalItems: 0,
      hasItemsWithActions: false,
      show: true,
      statuses: [],
      filterData: this.filter || [],
      internalFilterValue: this.filterValue || null,
      selectable: null,
      modalData: {},
      tableWidth: 0,
      additionalWidth: 0,
      tableActionsWidth: 0,
      defaultMethodComponents: {
        destroy: defaultDeleteConfirmation,
        delete: defaultDeleteConfirmation,
      },
      orderBy: {
        column: null,
        order: null,
      },
      columnOrder: false,
      rowOrder: false,
      existingColumns: [],
      existingRows: [],
      cache: {
        items: null,
        itemsPerPage: null,
        filter: null,
      },
      tableNum: window.baseTableNum,
      isMounted: false,
    };
  },

  computed: {
    tableSlug() {
      let tableSuffix = '';

      if (this.endpoint) {
        tableSuffix = this.endpoint + new URLSearchParams(this.endpointParams).toString();
      } else if (this.tableNum) {
        tableSuffix = this.tableNum;
      }

      return slugify(`${window.location.pathname}_${tableSuffix}`);
    },
    currentFilterValue: {
      get() {
        return this.internalFilterValue;
      },
      set(value) {
        this.internalFilterValue = value;
      },
    },
    hasError() {
      return this.errorMessage !== null;
    },
    modal: {
      get() {
        return this.modalData;
      },
      set({
            active, title, component, props, method, ...args
          }) {
        // eslint-disable-next-line no-undef
        const mergedProps = _.merge(this.modalData.props, props);

        this.modalData = {
          ...this.modalData,
          active,
          title: mergedProps.title || title,
          component,
          props: mergedProps,
          ...args,
        };

        this.$emit('update', method);
      },
    },
    hasAdditionalContent() {
      const additionalContentSlot = { ...this.$slots, ...this.$scopedSlots }.additionalContent;

      return typeof additionalContentSlot === 'function';
    },
    getFilter() {
      return {
        ...this.currentFilterValue,
        page: this.currentPage,
        size: this.perPage,
        ...(this.enableOrderBy ? {
          columnBy: this.orderBy.column,
          orderBy: this.orderBy.order,
        } : {}),
      };
    },
    slots() {
      return { ...this.$slots, ...this.$scopedSlots };
    },
    pageCount() { return Math.ceil(this.totalItems / this.perPage); },
    hasEndpoint() {
      return typeof this.endpoint !== 'undefined' && this.endpoint !== null && this.endpoint !== '';
    },
    hasItems() {
      return Array.isArray(this.items);
    },
    isAsync() {
      return this.hasEndpoint;
    },
    hasActions() {
      return this.showActions || (this.currentItems.filter((item) => {
        const itemActionKey = this.getItemActionKey(item);

        if (itemActionKey) {
          const itemActions = item[itemActionKey];

          if (typeof itemActions === 'string') {
            return `${itemActions}`.trim() !== '';
          } if (Array.isArray(itemActions)) {
            return itemActions.length > 0;
          }
        }

        return false;
      }).length > 0 && !this.disableActions);
    },
    isCheckable() {
      return this.currentItems.filter((item) => !!item.id).length > 0 && this.checkable;
    },
    visibleFields() {
      return this.existingColumns.filter((item) => item.visible);
    },
    selectedAllOnPage() {
      if (this.items) {
        this.calculateItems(this.items);
      }

      return this.currentItems.every((x) => this.selected.includes(x.id));
    },
    gridTemplate() {
      const gridTemplate = [];

      this.visibleFields.forEach((field) => {
        gridTemplate.push(field.size || '1fr');
      });

      if (this.hasActions) {
        gridTemplate.push('max-content');
      }

      return gridTemplate.join(' ');
    },
    showEmptyMessage() {
      return !this.currentItems.length;
    },
  },

  watch: {
    filterValue() {
      this.currentFilterValue = this.filterValue;
    },
    sid() {
      this.calculateTable(0);
    },
    value() {
      if (!this.value || NOT_EDITABLE_ACTIONS.indexOf(this.value) !== -1) {
        return;
      }

      this.resolveAction({
        type: 'modal',
        method: this.value,
        ...this.getMethodComponent(this.value).props,
      });
    },
    currentPage() {
      this.getItems();
    },
    perPage() {
      this.currentPage = 1;
      this.getItems();
    },
    currentItems: {
      handler(items) {
        this.$nextTick(() => {
          this.calculateTable();
          this.existingRows = items;
        });
      },
      deep: true,
    },
    hiddenColumns: {
      handler() {
        this.existingColumns = this.cache?.items || this.fields.filter((field) => !ACTION_KEYS.includes(field.key)).map((field) => ({
          ...field,
          visible: !this.hiddenColumns[field.key],
        }));
      },
      deep: true,
    },
    visibleFields: {
      handler() {
        this.$emit('updateColumns', this.visibleFields.reduce((acc, field) => ({
          ...acc,
          [field.key]: !this.hiddenColumns[field.key],
        }), {}));
        this.calculateTable();
      },
      deep: true,
    },
    currentFilterValue: {
      deep: true,
      handler() {
        if (this.filterInteraction) {
          this.currentPage = 1;
          this.$nextTick(() => this.getItems());
        }
      },
    },
    checked: {
      deep: true,
      handler() {
        this.selected = this.checked;
      },
    },
    error(val) {
      this.errorMessage = val;
    },
    selected() {
      this.errorMessage = null;
    },
    endpointParams: {
      deep: true,
      handler() {
        this.updateExistingColumns();
      },
    },
    checkAllParams: {
      deep: true,
      handler() {
        this.$nextTick(() => this.getItems());
      },
    },
    items: {
      deep: true,
      handler(items) {
        this.currentItems = items instanceof Array ? items : this.convertToArray(items);
      },
    },
    fields: {
      deep: true,
      handler() {
        this.updateExistingColumns();
      },
    },
    getFilter: {
      deep: true,
      handler() {
        this.$emit('updateFilters', this.getFilter);
      },
    },
    rowOrderPopup(value) {
      this.rowOrder = value;
    },
  },

  async mounted() {
    // Init selected values
    // eslint-disable-next-line no-undef
    this.selected = _.uniq((this.selected || []).concat(this.checked || []));

    if (this.selected.includes(0)) {
      this.changeSelected('all', true);
    } else {
      this.emitOnCheck();
    }

    window.addEventListener('resize', this.calculateTable);

    // eslint-disable-next-line no-undef
    GlobalEvents.listen(
        'update-data-table',
        async () => {
          this.$emit('update');
          if (this.pageCount === this.currentPage) {
            const items = await this.getItems(true);
            if (this.currentPage > 1 && items.length <= 0) {
              this.currentPage -= 1;
            }
          }
          this.getItems();
        },
    );

    let i = 0;
    let stored;
    const loop = async () => {
      if (i < 1 && stored === undefined) {
        await this.updateExistingColumns().then(() => {
          this.isMounted = true;
        }).finally(() => {
          i += 1;
          window.requestAnimationFrame(loop);
        });
      }
    };

    window.requestAnimationFrame(loop);
  },

  methods: {
    ...mapMutations('table', {
      SET_ITEM: 'SET_ITEM',
    }),
    ...mapMutations('baseForm', {
      SET_DEFAULTS: 'SET_DEFAULTS',
    }),

    updateExistingColumns() {
      return new Promise((resolve) => {
        appIDB.get(`table.${this.tableSlug}`).then((value) => {
          const stored = value;
          this.cache = stored || this.cache;
          this.perPage = this.cache?.itemsPerPage || this.itemsPerPage;

          // Get current fields excluding action keys
          const currentFields = this.fields.filter(
              (field) => !ACTION_KEYS.includes(field.key),
          );

          // If we have cached items, validate them against current fields
          if (this.cache?.items) {
            const validCachedFields = this.cache.items.filter(
                (cachedField) => currentFields.some(
                    (field) => field.key === cachedField.key,
                ),
            );

            // Exclude fields that are not in the current fields
            if (validCachedFields.length === currentFields.length) {
              this.existingColumns = validCachedFields;
            } else {
              // Otherwise use current fields
              this.existingColumns = currentFields.map((field) => ({
                ...field,
                visible: !this.hiddenColumns[field.key],
              }));
            }
          } else {
            // No cache, use current fields
            this.existingColumns = currentFields.map((field) => ({
              ...field,
              visible: !this.hiddenColumns[field.key],
            }));
          }

          this.getItems();
          resolve();
        });
      });
    },

    hasItem(field) {
      return this.currentItems.some(
          (item) => {
            if (field.showLabel) {
              return true;
            }
            return item[field.key] !== null
                && item[field.key] !== undefined;
          },
      );
    },

    action(type, data) {
      const optEvent = typeof type === 'string' ? type : type.method;
      const eventKey = typeof type !== 'string' && type.event ? type.event : optEvent;
      const eventValues = typeof type === 'object' ? type : data;

      this.SET_ITEM(['item', eventValues]);
      this.$emit(eventKey, eventValues);
      this.resolveAction(eventValues);
    },

    close() {
      this.$emit('update', '');
      this.modalData = {};
      this.SET_DEFAULTS({});
    },

    getMethodComponent(method) {
      if (typeof this.$attrs[method] !== 'undefined') {
        return this.$attrs[method];
      }

      return this.defaultMethodComponents[method] || {};
    },

    resolveAction({ type, method, ...props }) {
      if (type === 'modal') {
        this.modal = {
          active: true,
          method,
          ...this.getMethodComponent(`${method}`.trim() !== '' ? method : props.action),
          // A title sent with the action (e.g. from the backend) overrides the method default
          ...(typeof props.title === 'string' ? { title: props.title } : {}),
          props: {
            // eslint-disable-next-line no-undef
            ..._.merge(this.getMethodComponent(method).props, props),
            method: method || props.action,
          },
        };
      }
    },

    async changeStatus({ status, route, id }) {
      try {
        // eslint-disable-next-line no-undef
        const response = await axios.post(route, {
          id,
          status_id: status.id,
        });
        if (response.data.message) {
          const item = this.currentItems.find((x) => x.id === id);
          const index = this.currentItems.indexOf(item);
          const newStatus = this.statuses.find((x) => x.id === status.id);
          this.currentItems[index].status = newStatus.id;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      }
    },

    clearSelection() {
      this.pageSelection = [];
      this.selected = [];
      this.emitOnCheck();
    },

    getItemKey(item, inx) {
      return btoa(`row-${inx}-${JSON.stringify(item)}`);
    },

    isActionKey(key) {
      return ACTION_KEYS.includes(key);
    },

    itemHasActions(item) {
      const actionKey = this.getItemActionKey(item);
      if (!actionKey) return false;
      const value = item[actionKey];
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'string') return value.trim() !== '';
      return false;
    },
    getItemActionKey(item) {
      const keys = Object.keys(item);
      if (keys.includes('actions')) {
        return 'actions';
      } if (keys.includes('html_actions')) {
        return 'html_actions';
      } if (keys.includes('html_extra')) {
        return 'html_extra';
      }

      return null;
    },
    getRowClass(item, index) {
      if (typeof this.rowClass === 'function') {
        return this.rowClass(item, index);
      }
      if (typeof this.rowClass === 'string' && item[this.rowClass]) {
        return item[this.rowClass];
      }
      if (item.rowClass) {
        return item.rowClass;
      }
      return '';
    },

    selectAll(event) {
      if (this.checkAll) {
        const params = { ...this.getFilter, ...this.checkAllParams };

        // eslint-disable-next-line no-undef
        axios.get(this.checkAll, { params })
            .then((res) => {
              this.selected = res.data;
              this.emitOnCheck();
            })
            .catch((err) => {
              console.error(err);
            });
      } else {
        event.preventDefault();
        this.changeSelected('all', this.items || this.currentItems);
        this.emitOnCheck();
      }
    },
    change(payload) {
      this.currentPage = payload;
      this.$emit('change', payload);
    },
    emitOnCheck() {
      this.$emit('onCheck', this.selected);
    },
    changeSelected(name, value) {
      this.selectionSwitch(name, value);
      this.emitOnCheck();
    },
    selectionSwitch(name, value) {
      if (name === 'all') {
        const allSelected = value.every((x) => this.selected.includes(x.id));

        value.forEach((item) => this.selectionSwitch(item.id, !allSelected));
      } else if (value && this.selected.indexOf(name) === -1) {
        this.selected.push(name);
        this.pageSelection.push(name);
      } else if (!value && this.selected.indexOf(name) !== -1) {
        this.selected.splice(this.selected.indexOf(name), 1);
        this.pageSelection.splice(this.pageSelection.indexOf(name), 1);
      }
    },
    convertToArray(obj) {
      return Object.keys(obj).length > 0 ? [obj] : [];
    },
    calculateItems(items) {
      const end = this.perPage * this.currentPage;
      const start = this.perPage * (this.currentPage - 1);
      const itemsToCalculate = items instanceof Array ? items : this.convertToArray(items) || [];

      this.currentNumItems = this.perPage > itemsToCalculate.length ? itemsToCalculate.length : this.perPage;
      this.currentItems = itemsToCalculate.slice(start, end > itemsToCalculate.length ? itemsToCalculate.length : end);
    },
    getItems(simpleReturn = false) {
      if (!this.isAsync && this.hasItems && !simpleReturn) {
        const { items } = this;

        if (this.pagination) {
          this.calculateItems(items);
        } else {
          this.currentItems = items instanceof Array ? items : this.convertToArray(items) || [];
        }

        this.updateTotalItems(items.length || 0);

        this.calculateTable();

        return items;
      } if (!this.isAsync) {
        return {};
      }

      const params = { ...this.getFilter, ...this.endpointParams };

      this.isBusy = true;

      let { endpoint } = this;
      Object.keys(params).forEach((key) => {
        const regex = new RegExp(`[?&]${snakeCase(key)}.*`, 'g');
        endpoint = endpoint.replace(regex, '');
      });

      return new Promise((resolve, reject) => {
        // eslint-disable-next-line no-undef
        axios.get(endpoint, {
          params,
        }).then(({ data }) => {
          const items = data.items || [];
          this.isBusy = false;
          if (simpleReturn) {
            resolve(items);
            this.$emit('total', data.currentTotal || data.total || 0);
            this.showActions = data.showActions || false;
            this.$emit('totalAll', data.total || 0);
            this.$emit('fetched', data);
          } else {
            this.currentNumItems = items.length;

            this.currentItems = items;

            if (data.statuses) {
              this.statuses = data.statuses;
            }
            if (data.selectable) {
              this.selectable = data.selectable;
            }
            if (data.deleted) {
              this.SET_ITEM(['trash', data.deleted]);
            }
            if (data.filter) {
              if (Object.keys(this.filter || {}).length) {
                this.$emit('getFilters', this.filterData);
              } else {
                this.filterData = data.filter instanceof Array ? data.filter : [];
                this.$emit('getFilters', this.filterData);
              }
            }

            this.updateTotalItems(data.currentTotal || data.total || 0);

            this.calculateTable();

            resolve(items);
            this.$emit('total', data.currentTotal || data.total || 0);
            this.$emit('totalAll', data.total || 0);
            this.showActions = data.showActions || false;
            this.$emit('fetched', data);
          }
        }).catch((response) => {
          this.isBusy = false;
          this.currentNumItems = 0;

          reject(response);
        });
      });
    },

    setVariables() {
      this.tableWidth = null;
      this.tableActionsWidth = null;

      this.$nextTick(() => {
        const {
          table,
          tableAction,
        } = this.$refs;

        if (table) {
          this.tableWidth = table.offsetWidth;
          this.additionalWidth = this.additionalFixed ? table.offsetWidth : 'unset';
        }

        if (this.hasActions && tableAction) {
          this.tableActionsWidth = tableAction.offsetWidth;
        } else {
          this.tableActionsWidth = 0;
        }
      });
    },

    handleKeydown(event, el) {
      event.preventDefault();
      event.stopPropagation();
      // Trigger element click (not forced navigation) so modal anchors (data-bs-toggle, href="javascript:;") work via keyboard — mirrors mouse-click path.
      (el || event.currentTarget || event.target).click();
    },

    calculateTable(delay = 100) {
      this.$nextTick(() => this.setVariables);
      setTimeout(this.setVariables, delay);
      this.$nextTick(() => {
        document.querySelectorAll('.html-extra a').forEach((el) => {
          // Guard double-bind on anchors: calculateTable reruns on resize/data change; stacked listeners would toggle Bootstrap modal open then shut.
          if (el.getAttribute('data-keyboard-activation-bound') === 'true') {
            return;
          }
          el.setAttribute('data-keyboard-activation-bound', 'true');
          el.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              this.handleKeydown(event, el);
            }
          });
        });
      });
    },

    updateTotalItems(count) {
      this.totalItems = count;
      document.querySelectorAll(`span[data-table-total="${this.sid}"]`).forEach((el) => {
        // eslint-disable-next-line no-param-reassign
        el.innerText = count;
      });
    },

    async savePerPage(e) {
      this.cache.itemsPerPage = e.target.value;
      await appIDB.set(`table.${this.tableSlug}`, this.cache);
    },

    hasSlot(name) {
      return !!this.$slots[name];
    },
    getGridRow(index) {
      const headerOffset = this.hideHeader ? 0 : 1;
      const baseRow = index + 1 + headerOffset + index;
      // If prepend slot exists, offset data rows by 1 to make room for prepend
      const prependOffset = this.slots.prepend ? 1 : 0;
      return baseRow + prependOffset;
    },
    isLastItem(index) {
      return index === this.visibleFields.length - (this.hasActions ? 2 : 1);
    },
    isLastRow(index) {
      return index === this.currentItems.length - 1;
    },
    sort(field) {
      if (!this.enableOrderBy) return;
      if (field.disableOrderBy === true) return;

      let order = 'asc';
      const column = field.orderByKey || field.key;

      if (this.orderBy.column === column) {
        order = this.orderBy.order === 'asc' ? 'desc' : 'asc';
      }

      this.orderBy = {
        column,
        order,
      };

      this.getItems();
    },
    getSorting(field) {
      const column = field.orderByKey || field.key;

      if (this.orderBy.column === column) {
        return this.orderBy.order || 'none';
      }

      return 'none';
    },
    async changeOrder(items) {
      this.existingColumns = items;
      this.columnOrder = false;
      this.cache.items = items;
      await appIDB.set(`table.${this.tableSlug}`, this.cache);
    },
    async changeRowOrder(items) {
      // eslint-disable-next-line no-undef
      await axios.post(this.checkAll, { items });
      this.closeRowOrderModal();
      this.getItems();
    },
    closeRowOrderModal() {
      this.$emit('closeRowOrderModal');
    },
  },
};
</script>

<style lang="scss" scoped>
.table-invalid {
  .base-checkbox {
    :deep(.base-checkbox--input) {
      box-shadow: 0 0 5px 0 var(--base-input--error-color);
    }
  }

  .table-hint {
    display: flex;
    justify-content: flex-end;
    grid-column-gap: 1em;
    padding: 0.25em 0;

    &--error {
      flex: 1;
      display: block;
      font-size: 12px;
      color: var(--base-input--error-color);
      line-height: 1.25;
    }

    .table-hint--error {
      color: var(--base-input--error-color);
    }
  }
}

.base-data-table {
  --base-data-table-row-color: #f8f7f6;
  --base-data-table-action-background: #FFFFFF;
  --base-data-table-action-shadow: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  --base-data-table-action-shadow-default: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, transparentize(#707070, .5) 100%);

  &.inverted {
    --base-data-table-row-color: #FFFFFF;
    --base-data-table-action-background: #f8f7f6;
    --base-data-table-action-shadow: linear-gradient(90deg, transparentize(#f8f7f6, 1) 0%, #f8f7f6 100%);
  }

  &__content {
    display: grid;
    grid-gap: 0;
    grid-template-columns: var(--table-grid-template-columns);
    position: relative;
    overflow: auto;

    .column-reorder {
      pointer-events: all;
    }

    .table-item {
      white-space: nowrap;

      .base-checkbox--input {
        width: 16px;
        height: 16px;

        &-icon {
          font-size: 0.675em;
        }
      }

      &__data, &__label {
        padding: 10px 20px;
      }

      &__label {
        padding-top: 3px; // Padding for checkbox for page selection (hover/error box-shadow)
        font-weight: 700;

        &.last-field + .table-item__action:before {
          background: var(--base-data-table-action-shadow);
        }

        &__sort-icon {
          font-size: 12px;
          padding-right: 5px;

          &[data-order="desc"] {
            i, svg {
              transform: rotate(180deg);
            }
          }

          &, & + span {
            cursor: pointer;
          }
        }
      }

      &__data {
        background-color: var(--base-data-table-row-color);

        &-additional {
          padding-bottom: 10px;
          grid-column: -1 / 1;

          &.last-row {
            padding-bottom: 0;
          }

          > div {
            width: 100%;
            max-width: var(--table-additional-width);
            padding-right: var(--table-actions-width);
            position: sticky;
            left: 0;
            background-color: var(--base-data-table-row-color);
            white-space: normal;

            &:not(:empty) {
              padding: 0 50px 10px 20px;
            }
          }
        }
      }

      &__action {
        position: sticky;
        right: 0;
        background-color: var(--base-data-table-action-background);
        z-index: 1;

        &:before {
          position: absolute;
          content: '';
          display: block;
          top: 0;
          height: 100%;
          right: 100%;
          width: .625em;
          background: var(--base-data-table-action-shadow-default);
        }

        &.no-shadow:before {
          width: 1.25em;
          background: var(--base-data-table-action-shadow);
        }

        > button.btn-options {
          padding: 10px 20px;
          margin: 0 auto;
        }

        > .html-extra {
          padding: 10px 20px;
          margin: 0 auto;
        }

        > .html-extra, > .btn-options {
          text-align: center;
        }
      }

      &__empty {
        background-color: var(--base-data-table-row-color);
        padding: 10px 20px;
        text-align: center;
        grid-column: -1 / 1;
      }

      &.last-field {
        padding-right: calc(var(--table-actions-width) + 20px);
      }
    }
  }

  &.with-actions .base-data-table__content {
    width: calc(100% + 20px);
  }

  .io-alert {
    z-index: 10;
    position: relative;
  }
}

.gear {
  display: flex;
  justify-content: center;
  margin-top: 5px;

  svg {
    cursor: pointer;
    transition: 1s;

    &:hover {
      transform: rotate(180deg);
    }
  }
}
</style>
